import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from "react-router-dom";
import i18next from 'i18next';

import { OpSenseToastContainer, toast } from '../Toast';
import { PageTitle } from '../PageTitle';
import AssetForm from './asset_form';
import { AssetsService, ContentService } from '../../services';

const mapStateToProps = (state) => ({ me: state.user.me });

const AssetEdit = ({ me, match, location }) => {
  const enterprise = me.enterprise;

  const [savedAssetId, setSavedAssetId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [asset, setAsset] = useState(null);
  const [floorPlanURL, setFloorPlanURL] = useState(null);
  const [floorplanLocation, setFloorplanLocation] = useState(null);
  const [assetImages, setAssetImages] = useState([]);

  useEffect(() => {
    AssetsService.get(match.params.assetId).then((response) => {
      ContentService.getDeviceGroupingFloorplanImage(response.asset.businessGroup.id).then(floorPlanURL => {
        const imageFetches = response.asset.assetImages.map(async image => ({
          id: image.id, 
          name: image.name,
          description: image.description,
          src: await ContentService.getAssetImage(response.asset.id, image.name), 
          primary: image.primary && image.primary, 
          original: true, 
          deleted: false, 
          replaced: false
        }));

        Promise.all(imageFetches).then(fetchedAssetImages => {
          setFloorPlanURL(floorPlanURL);
          setFloorplanLocation({ x: response.asset.floorPlanX, y: response.asset.floorPlanY });
          setAssetImages(fetchedAssetImages);
          setAsset(response.asset);          
        });      
      });
    }).catch((error) => {
      console.error(error);
      toast.error(i18next.t('messages.could-not-load-asset-edit'));
    });
  }, [me, match]);

  const handleSubmit = useCallback((assetFormData) => {
    setSaving(true);

    let tags = assetFormData.genericTags.map((tag) => ({id: tag.value}));
    if (assetFormData.areaTag) {
      tags.push({id: assetFormData.areaTag.value});
    }

    const metadata = asset.assetType.assetTypeMetadataTypes.map((metadataType) => {
      if (metadataType.stringValueFlag) {
        return ({ assetTypeMetadataType: {id: metadataType.id}, stringValue: assetFormData[`metadata-${metadataType.id}`] });
      } else {
        return ({ assetTypeMetadataType: {id: metadataType.id}, numericValue: assetFormData[`metadata-${metadataType.id}`] });
      }
    });

    let measures = assetFormData.measures.filter((measure) => (measure.device != null))
                                           .map((measure) => ({
                                              device: {id: measure.device.id}, 
                                              deviceDataType: {
                                                id: measure.deviceDataType.id,
                                              },
                                              assetTypeDeviceDataType: {
                                                id: measure.assetTypeDeviceDataType.id,
                                                deviceDataClassification: { id: measure.assetTypeDeviceDataType.deviceDataClassification.id }
                                              },
                                              startAt: measure.startAt
                                            }));

    assetFormData.removedMeasures.forEach((removedMeasure) => {
      measures.push({ id: removedMeasure.id,
                      device: {id: removedMeasure.device.id}, 
                      deviceDataType: {
                        id: removedMeasure.deviceDataType.id 
                      },
                      assetTypeDeviceDataType: {
                        id: removedMeasure.assetTypeDeviceDataType.id
                      }, 
                      startAt: removedMeasure.startAt,
                      endAt: removedMeasure.endAt
                    });
    });

    let updatedAsset = {
      id: asset.id,
      enterprise: {id: enterprise.id},
      name: assetFormData.name,
      archived: false,
      assetType: { id: asset.assetType.id },
      businessGroup: { id: asset.businessGroup.id },
      tags: tags,
      assetMeasures: measures,
      assetMetadata: metadata,
      floorPlanX: floorplanLocation != null ? floorplanLocation.x : -1,
      floorPlanY: floorplanLocation != null ? floorplanLocation.y : -1,
      assetImages: assetImages.filter((image) => image.id != null).map((image) => ({ id: image.id, name: image.name, description: image.description }))
    };

    const newImages = assetImages.filter((image) => image.id == null && image.replacesId == null);
    const replacementImages = assetImages.filter((image) => image.replacesId != null);
    const deletedImages = assetImages.filter((image) => image.id != null && image.deleted);
  
    AssetsService.update(updatedAsset).then(response => {      
      const uploads = newImages.map((assetImage) => AssetsService.uploadImage(response.asset.id, assetImage.file));
      const replacements = replacementImages.map((assetImage) => AssetsService.replaceImage(response.asset.id, assetImage.file, assetImage.replacesId));
      const deletes = deletedImages.map((assetImage) => AssetsService.deleteImage(response.asset.id, assetImage.name, assetImage.id));

      Promise.all(uploads.concat(replacements).concat(deletes)).then(_ => {
        setSavedAssetId(response.asset.id);
        setSaving(false);
      }).catch(error => {
        console.error(error);
        setSaving(false);
        toast.error(i18next.t('messages.asset-image-upload-failure'));
      });
    }).catch(error => {
      console.error(error);      
      setSaving(false);
      toast.error(error.message);
    });
  });
  
  if (savedAssetId) {
    return (
        <Redirect to={{
          pathname: `/config/assets/view/${savedAssetId}`,
          state: {
            from: location,
            successMessage: i18next.t('messages.asset-saved')
          }
        }}/>
    );
  } else {
    if (asset && assetImages && floorPlanURL && !saving) {
      const backLinkSearch = location.state && location.state.backLinkSearch ? location.state.backLinkSearch : null;
      const searchValue = location.state && location.state.searchValue ? location.state.searchValue : null;
      return (
        <div className="assets">
          <OpSenseToastContainer />

          <div className="row">
            <PageTitle title={i18next.t('pages.assets.edit-asset')}
                        backLinkWithParams={{
                          link: {
                            pathname: "/config/assets",
                            search: backLinkSearch,
                            state: { searchValue: searchValue }
                          }
                        }} />
          </div>

          <AssetForm
              asset={asset}
              onSubmit={handleSubmit}
              onFloorplanLocationChange={setFloorplanLocation}
              onAssetImageChange={setAssetImages}
              floorPlanURL={floorPlanURL}
              assetImages={assetImages}
              backLinkWithParams={{
                link: {
                  pathname: "/config/assets",
                  search: backLinkSearch,
                  state: { searchValue: searchValue }
                }
              }}
          />
        </div>
      );
    } else {
      return (
        <div>
          <OpSenseToastContainer />

          <div className="loader">{i18next.t('loading')}</div>
        </div>
      );
    }
  }
}

export default connect(mapStateToProps)(AssetEdit);
